import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";

// We store handymen in the vendors collection
const COLLECTION_NAME = "vendors";

// Handyman model object (mirrors the style used in other models like userModel)
const handymanModel = {
  async create(handymanData) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");

    const handyman = {
      ...handymanData,
      ownerId: user.uid,
      isActive: true,
      totalJobs: 0,
      rating: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), handyman);
    return {
      success: true,
      handyman: { id: docRef.id, ...handyman },
      message: "Handyman created successfully",
    };
  },

  async getByOwner(ownerId = null) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const userId = ownerId || user.uid;
    const q = query(
      collection(db, COLLECTION_NAME),
      where("ownerId", "==", userId),
      where("isActive", "==", true),
      orderBy("name", "asc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate() || new Date(),
      updatedAt: d.data().updatedAt?.toDate() || new Date(),
    }));
  },

  async getById(handymanId) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = doc(db, COLLECTION_NAME, handymanId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Handyman not found");
    const data = snap.data();
    return {
      id: snap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  },

  async update(handymanId, updates) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = doc(db, COLLECTION_NAME, handymanId);
    await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
    return {
      success: true,
      handyman: { id: handymanId, ...updates, updatedAt: new Date() },
      message: "Handyman updated successfully",
    };
  },

  async delete(handymanId) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = doc(db, COLLECTION_NAME, handymanId);
    await updateDoc(ref, { isActive: false, updatedAt: serverTimestamp() });
    return { success: true, message: "Handyman deleted successfully" };
  },

  async getBySpecialty(specialty) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const q = query(
      collection(db, COLLECTION_NAME),
      where("ownerId", "==", user.uid),
      where("isActive", "==", true),
      where("specialties", "array-contains", specialty),
      orderBy("name", "asc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate() || new Date(),
      updatedAt: d.data().updatedAt?.toDate() || new Date(),
    }));
  },

  async updateRating(handymanId, newRating) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const ref = doc(db, COLLECTION_NAME, handymanId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Handyman not found");
    const data = snap.data();
    const currentRating = data.rating || 0;
    const totalJobs = data.totalJobs || 0;
    const newAverageRating =
      (currentRating * totalJobs + newRating) / (totalJobs + 1);
    await updateDoc(ref, {
      rating: newAverageRating,
      totalJobs: totalJobs + 1,
      updatedAt: serverTimestamp(),
    });
    return {
      success: true,
      handyman: {
        id: handymanId,
        rating: newAverageRating,
        totalJobs: totalJobs + 1,
        updatedAt: new Date(),
      },
      message: "Handyman rating updated successfully",
    };
  },

  async getStats() {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const q = query(
      collection(db, COLLECTION_NAME),
      where("ownerId", "==", user.uid),
      where("isActive", "==", true)
    );
    const snapshot = await getDocs(q);
    const handymen = snapshot.docs.map((d) => d.data());
    const stats = {
      total: handymen.length,
      bySpecialty: {},
      averageRating: 0,
      totalJobs: 0,
    };
    let totalRating = 0;
    let rated = 0;
    handymen.forEach((h) => {
      h.specialties?.forEach((s) => {
        stats.bySpecialty[s] = (stats.bySpecialty[s] || 0) + 1;
      });
      if (h.rating > 0) {
        totalRating += h.rating;
        rated++;
      }
      stats.totalJobs += h.totalJobs || 0;
    });
    stats.averageRating = rated > 0 ? totalRating / rated : 0;
    return stats;
  },
};

export default handymanModel;
